import type { ProgressiveSentenceNotesArgs } from './useProgressiveSentenceNotes.types';
import type { useSentenceNotesReduxActions } from 'shared/features/sentenceNotes/hooks';
import type {
  NormalizedSentenceNotesResponse,
  NormalizedSystemGeneratedSentenceNotesResponse
} from '../../sentenceNotes.types';
import type { ProgressiveSentenceNotesData } from './useProgressiveSentenceNotesData.types';

import * as constants from './useProgressiveSentenceNotes.constants';
import * as sentenceNotesConstants from '../../sentenceNotes.constants';
import * as sentenceNotesApi from '../../sentenceNotes.api';
import * as sentenceNotesHelpers from '../../sentenceNotes.helpers';
import * as reducerHelpers from '../../sentenceNotes.reducer.helpers';

export const getRequestParams = ({
  args,
  prevPageParams
}: {
  args: Pick<ProgressiveSentenceNotesArgs, 'documentId' | 'type'>;
  prevPageParams: Parameters<
    ReturnType<typeof useSentenceNotesReduxActions>['getSentenceNotes']
  >[0]['params'];
}): Parameters<
  ReturnType<typeof useSentenceNotesReduxActions>['getSentenceNotes']
>[0]['params'] => {
  return {
    ...constants.INITIAL_PARAMS,
    ...prevPageParams,
    document_id: args.documentId,
    is_obligation: args.type === sentenceNotesConstants.SENTENCE_NOTE_TYPE.OBLIGATIONS
  };
};

export const getNextPageParam =
  (limit: number) =>
  <T extends { offset: number; total: number }>(
    lastPage: T,
    _: T[],
    lastPageParam: unknown
  ): Record<string, unknown> | undefined => {
    if (lastPage.offset + limit >= lastPage.total) {
      return undefined;
    }
    return {
      ...(lastPageParam as Record<string, unknown>),
      offset: lastPage.offset + limit
    };
  };

export const fetchSentenceNotes = async ({
  params,
  args,
  events
}: {
  params: Parameters<typeof sentenceNotesApi.getSentenceNotes>[0];
  args: Pick<ProgressiveSentenceNotesArgs, 'skipReduxStorage'>;
  events: {
    onSentenceNotesFetch: ReturnType<typeof useSentenceNotesReduxActions>['getSentenceNotes'];
    onSetLocalSentenceNotes: ProgressiveSentenceNotesData['localActions']['setLocalSentenceNotes'];
  };
}): Promise<NormalizedSentenceNotesResponse> => {
  if (!args.skipReduxStorage) {
    return events.onSentenceNotesFetch({ params });
  }

  const response = await sentenceNotesApi.getSentenceNotes(params);
  const normalizedResponse = sentenceNotesHelpers.normalizeSentenceNotesResponse(response);
  events.onSetLocalSentenceNotes(prev =>
    reducerHelpers.mergeSentenceNotes(prev, normalizedResponse.sentenceNotes)
  );
  return normalizedResponse;
};

export const fetchSystemGeneratedNotes = async ({
  args,
  events
}: {
  args: Pick<ProgressiveSentenceNotesArgs, 'skipReduxStorage' | 'documentId'>;
  events: {
    onSystemGeneratedNotesFetch: ReturnType<
      typeof useSentenceNotesReduxActions
    >['fetchSystemGeneratedSentenceNotes'];
    onSetLocalSentenceNotes: ProgressiveSentenceNotesData['localActions']['setLocalSentenceNotes'];
  };
}): Promise<NormalizedSystemGeneratedSentenceNotesResponse> => {
  if (!args.skipReduxStorage) {
    return events.onSystemGeneratedNotesFetch({ documentId: args.documentId });
  }

  const response = await sentenceNotesApi.fetchSystemGeneratedSentenceNotes(args.documentId);
  const normalizedResponse =
    sentenceNotesHelpers.normalizeSystemGeneratedSentenceNotesResponse(response);
  events.onSetLocalSentenceNotes(prev =>
    reducerHelpers.mergeSentenceNotes(prev, normalizedResponse.sentenceNotes, {
      useUniqueBySentences: true
    })
  );
  return normalizedResponse;
};
